import { claimSpeechAuthorization, voiceAuthorityRefusal, isVoiceGovernanceRefusal, admittedBrowserFallback } from "./speechAuthorization";

// client/src/voice/realtimeClient.js

/**
 * Sherloc Voice – Eden TTS helper (Client / Cortex).
 *
 * Client now calls the backend at /api/voice/greeting,
 * instead of hitting OpenAI directly.
 *
 * The backend:
 *   - Handles OPENAI_API_KEY securely
 *   - Uses the "nova" voice for TTS
 *   - Differentiates portal: "superadmin" vs "client"
 */

// Base URL for your backend – Cortex uses REACT_APP_BACKEND_URL
const API_BASE_URL =
  process.env.REACT_APP_BACKEND_URL ||
  (typeof window !== 'undefined' ? window.location.origin : '');

// Debug log
console.log('[SherlocVoice][Client] API_BASE_URL for voice:', API_BASE_URL);

/**
 * EDEN Voice endpoints are authenticated. Reuse the portal's existing
 * session credential; requests without it fail closed server-side and the
 * caller falls back to browser TTS / text.
 */
function resolveVoiceAuthToken() {
  try {
    return (
      window.localStorage.getItem('clientAuthToken') ||
      window.localStorage.getItem('authToken') ||
      null
    );
  } catch (_err) {
    return null;
  }
}

function voiceRequestHeaders() {
  const token = resolveVoiceAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

/**
 * Play an MP3 audio buffer in the browser via HTMLAudioElement.
 */
async function playAudioBufferAsMp3(arrayBuffer) {
  return playAudioBufferAsMp3WithHooks(arrayBuffer, {});
}

async function playAudioBufferAsMp3WithHooks(arrayBuffer, { onStart, onEnd, onError } = {}) {
  try {
    const blob = new Blob([arrayBuffer], { type: 'audio/mpeg' });
    const url = URL.createObjectURL(blob);

    const audio = new Audio(url);
    return await new Promise((resolve) => {
      let settled = false;
      const finish = (ok, error = null) => {
        if (settled) return;
        settled = true;
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
        if (!ok && typeof onError === 'function') {
          onError(error);
        }
        resolve({ ok, audio, error });
      };

      audio.onplay = () => {
        if (typeof onStart === 'function') onStart(audio);
      };
      audio.onended = () => {
        if (typeof onEnd === 'function') onEnd();
        finish(true);
      };
      audio.onerror = (event) => {
        console.error('[SherlocVoice][Client] Error playing audio:', event);
        finish(false, event);
      };

      audio
        .play()
        .catch((err) => {
          console.error('[SherlocVoice][Client] Error playing audio:', err);
          finish(false, err);
        });
    });
  } catch (err) {
    console.error(
      '[SherlocVoice][Client] Failed to play audio buffer:',
      err
    );
    if (typeof onError === 'function') onError(err);
    return { ok: false, audio: null, error: err };
  }
}

/**
 * 🔊 Speak greeting via backend for Cortex.
 *
 * The backend uses:
 *  - portal === "client" → "Welcome..."
 *  - voice === "nova"
 */
export async function speakGreeting(firstName) {
  const cleanName =
    firstName && String(firstName).trim().length > 0
      ? String(firstName).trim()
      : null;

  try {
    const res = await fetch(`${API_BASE_URL}/api/voice/greeting`, {
      method: 'POST',
      headers: voiceRequestHeaders(),
      body: JSON.stringify({
        firstName: cleanName,
        portal: 'client', // 👈 tells backend this is Cortex
      }),
    });

    if (!res.ok) {
      const errText = await res.text();
      console.error(
        '[SherlocVoice][Client] /api/voice/greeting error:',
        res.status,
        errText
      );
      return;
    }

    const audioBuffer = await res.arrayBuffer();
    await playAudioBufferAsMp3(audioBuffer);
  } catch (err) {
    console.error('[SherlocVoice][Client] speakGreeting error:', err);
  }
}

export async function speakWithPremiumVoice({
  text,
  portal = 'client',
  purpose = 'reply',
  speechAuthorization,
  isCurrent = () => true,
  onStart,
  onEnd,
  onError,
} = {}) {
  const spoken = String(text || '').trim();
  if (!spoken) {
    return { ok: false, reason: 'missing_text', provider: 'none', audio: null };
  }

  if (!isCurrent() || !claimSpeechAuthorization(speechAuthorization, spoken)) {
    return voiceAuthorityRefusal();
  }

  try {
    const res = await fetch(`${API_BASE_URL}/api/voice/speak`, {
      method: 'POST',
      headers: voiceRequestHeaders(),
      body: JSON.stringify({
        text: spoken,
        portal,
        purpose,
        speechAuthorization,
      }),
    });

    if (!isCurrent()) return voiceAuthorityRefusal("speech_request_superseded");
    if (!res.ok) {
      let body = null;
      try { body = await res.json(); } catch {}
      const governanceRefused = isVoiceGovernanceRefusal(res, body);
      const browserFallbackAllowed = !governanceRefused && admittedBrowserFallback(body);
      return {
        ok: false,
        reason: res.status === 401 || res.status === 403 ? "unauthorized" : (body?.refusalReason || body?.reason || "premium_tts_failed"),
        code: body?.code || null,
        status: res.status,
        governanceRefused,
        browserFallbackAllowed,
        provider: browserFallbackAllowed ? "browser_fallback" : "none",
        audio: null,
      };
    }

    // Successful bytes still need the Backend's explicit non-reporting admission.
    const browserFallbackAllowed =
      res.headers.get("X-Voice-Classification") === "NON_REPORTING" &&
      res.headers.get("X-Voice-Browser-Fallback-Allowed") === "true";
    if (!browserFallbackAllowed) return voiceAuthorityRefusal("speech_admission_missing");
    const provider = res.headers.get('X-Eden-TTS-Provider') || 'openai';
    const voice = res.headers.get('X-Eden-Voice-Id') || 'unknown';
    const model = res.headers.get('X-Eden-TTS-Model') || 'unknown';
    console.log('[SherlocVoice][Client] premium_tts_ready', { provider, voice, model });

    const audioBuffer = await res.arrayBuffer();
    if (!isCurrent()) return voiceAuthorityRefusal("speech_request_superseded");
    const playback = await playAudioBufferAsMp3WithHooks(audioBuffer, {
      onStart,
      onEnd,
      onError,
    });

    return {
      ok: playback.ok,
      browserFallbackAllowed,
      reason: playback.ok ? 'premium_playback_ok' : 'premium_playback_failed',
      provider,
      audio: playback.audio || null,
      error: playback.error || null,
    };
  } catch (err) {
    console.warn('[SherlocVoice][Client] premium_tts_error', err);
    if (typeof onError === 'function') onError(err);
    return { ok: false, reason: 'premium_request_failed', browserFallbackAllowed: false, provider: 'none', audio: null, error: err };
  }
}

/**
 * Stub for future full text+TTS pipeline.
 */
export async function sendSherlocText(_userText, _systemPrompt) {
  console.warn(
    '[SherlocVoice][Client] sendSherlocText() is not implemented yet.'
  );
  return 'Sherloc Voice text pipeline is not wired yet.';
}
